import SubmissionCurrencyTab from 'components/features/submission-currency-tabs';
import { DEFAULT_CONVERSION_CURRENCY } from 'components/features/submission-details/types';
import ExcelTable from 'components/ui/excel-table';
import { TOTAL_ROW_KEY } from 'components/ui/excel-table/constants';
import {
  ExcelTableColDef,
  ECellValueType,
} from 'components/ui/excel-table/types';
import FormSection from 'components/ui/form-section';
import { ECurrencies } from 'constants/enums';
import {
  BinderClaimFinancialSummaryResponseDto,
  FinancialSummaryRowDto,
} from 'libs/pinnacle-sdk';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type FinancialSummaryRowData = {
  id: string;
  lossType: string;
  previousIncurredOurLayer: number;
  previousIncurredOurShare: number;
  paymentsOurLayer: number;
  paymentsOurShare: number;
  outstandingOurLayer: number;
  outstandingOurShare: number;
  newIncurredOurLayer: number;
  newIncurredOurShare: number;
};

export interface BinderClaimsFinancialSummaryProps {
  financialSummaryData?: BinderClaimFinancialSummaryResponseDto;
}

const BinderClaimsFinanacialSummary: React.FC<
  BinderClaimsFinancialSummaryProps
> = ({ financialSummaryData }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>(
    DEFAULT_CONVERSION_CURRENCY
  );

  const tableData = useMemo<FinancialSummaryRowData[]>(() => {
    if (!financialSummaryData?.financialSummary) {
      return [];
    }

    return financialSummaryData.financialSummary.map(
      (row: FinancialSummaryRowDto) => ({
        id: row.lossType,
        lossType: row.lossType,
        previousIncurredOurLayer: row.previousIncurredOurLayer
          ? Number(row.previousIncurredOurLayer)
          : 0,
        previousIncurredOurShare: row.previousIncurredOurShare
          ? Number(row.previousIncurredOurShare)
          : 0,
        paymentsOurLayer: row.paymentsOurLayer
          ? Number(row.paymentsOurLayer)
          : 0,
        paymentsOurShare: row.paymentsOurShare
          ? Number(row.paymentsOurShare)
          : 0,
        outstandingOurLayer: row.outstandingOurLayer
          ? Number(row.outstandingOurLayer)
          : 0,
        outstandingOurShare: row.outstandingOurShare
          ? Number(row.outstandingOurShare)
          : 0,
        newIncurredOurLayer: row.newIncurredOurLayer
          ? Number(row.newIncurredOurLayer)
          : 0,
        newIncurredOurShare: row.newIncurredOurShare
          ? Number(row.newIncurredOurShare)
          : 0,
      })
    );
  }, [financialSummaryData]);

  const summaryRowData = useMemo<FinancialSummaryRowData | undefined>(() => {
    if (!financialSummaryData?.total) {
      return undefined;
    }

    const total = financialSummaryData.total;
    return {
      id: TOTAL_ROW_KEY,
      lossType: t('general.total'),
      previousIncurredOurLayer: total.previousIncurredOurLayer
        ? Number(total.previousIncurredOurLayer)
        : 0,
      previousIncurredOurShare: total.previousIncurredOurShare
        ? Number(total.previousIncurredOurShare)
        : 0,
      paymentsOurLayer: total.paymentsOurLayer
        ? Number(total.paymentsOurLayer)
        : 0,
      paymentsOurShare: total.paymentsOurShare
        ? Number(total.paymentsOurShare)
        : 0,
      outstandingOurLayer: total.outstandingOurLayer
        ? Number(total.outstandingOurLayer)
        : 0,
      outstandingOurShare: total.outstandingOurShare
        ? Number(total.outstandingOurShare)
        : 0,
      newIncurredOurLayer: total.newIncurredOurLayer
        ? Number(total.newIncurredOurLayer)
        : 0,
      newIncurredOurShare: total.newIncurredOurShare
        ? Number(total.newIncurredOurShare)
        : 0,
    };
  }, [financialSummaryData, t]);

  const columnDefs = useMemo<
    ExcelTableColDef<FinancialSummaryRowData, string | number>[]
  >(() => {
    const columns: ExcelTableColDef<
      FinancialSummaryRowData,
      string | number
    >[] = [
      {
        field: 'lossType',
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'previousIncurredOurLayer',
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'previousIncurredOurShare',
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'paymentsOurLayer',
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'paymentsOurShare',
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'outstandingOurLayer',
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'outstandingOurShare',
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'newIncurredOurLayer',
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'newIncurredOurShare',
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
    ];

    return columns.map((c) => ({
      ...c,
      headerName: t(
        `claim.binderClaimsActions.binderClaimsFinancialSummary.${c.field}`
      ),
    }));
  }, [t]);

  return (
    <div>
      <SubmissionCurrencyTab
        originalCurrency={ECurrencies.AUD}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        className="flex justify-end"
      />
      <FormSection
        title={t(
          'claim.binderClaimsActions.binderClaimsFinancialSummary.title'
        )}
        collapsible
      >
        <ExcelTable
          rowKeyColName="id"
          columnDefs={columnDefs}
          data={tableData}
          summaryRowData={summaryRowData}
          includeAddRow={false}
          includeDeleteCol={false}
          includeIdRow={false}
          containerClassName="rounded-lg"
          detailRowAutoHeight={true}
          pagination={false}
          domLayout="autoHeight"
        />
      </FormSection>
    </div>
  );
};

export default BinderClaimsFinanacialSummary;
